/**
 * Bande-son des animations JRAF : musique d'ambiance (très basse) et jingles
 * du début et de la fin, choisis en base (bibliothèque `animation_audio`,
 * migration 0669 ; fichiers dans le bucket PUBLIC `animation-audio`).
 *
 * Lecture (élèves, visiteurs) : `get_animation_soundtrack()` ne renvoie que
 * les sons par défaut. Sans jingle choisi en base, les jingles JeRaisonne
 * livrés avec l'app jouent (`/animation-audio/…`, créés pour l'occasion,
 * libres de droits) ; sans musique choisie : pas de musique.
 *
 * Admin : liste, téléversement, son par défaut, volume, suppression ;
 * import des musiques de la médiathèque YouTube (bucket privé
 * `youtube-assets`, lisible par les admins) et installation des jingles livrés.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import type { AnimationSoundtrack } from '@jeraisonne/ui/components/animations/animation-player';
import { supabase } from '@/lib/supabase';

/** `animation_audio` (0669) n'est pas dans les types générés : client non typé. */
const db = supabase as unknown as SupabaseClient;

export const ANIMATION_AUDIO_BUCKET = 'animation-audio';

export type AnimationAudioKind = 'music' | 'jingle_intro' | 'jingle_outro';

export const AUDIO_KIND_LABEL: Record<AnimationAudioKind, string> = {
  music: 'Musique d’ambiance',
  jingle_intro: 'Jingle de début',
  jingle_outro: 'Jingle de fin',
};

/** Volume conseillé à l'ajout : la musique reste très basse sous la voix. */
export const DEFAULT_GAIN: Record<AnimationAudioKind, number> = {
  music: 0.12,
  jingle_intro: 0.55,
  jingle_outro: 0.55,
};

/** Jingles JeRaisonne livrés avec l'app (repli quand la base n'en a pas). */
export const BUNDLED_JINGLES: Record<
  'jingle_intro' | 'jingle_outro',
  { file: string; label: string }
> = {
  jingle_intro: { file: 'jingle-intro.mp3', label: 'Jingle JeRaisonne — début' },
  jingle_outro: { file: 'jingle-outro.mp3', label: 'Jingle JeRaisonne — fin' },
};

export function bundledJingleUrl(kind: 'jingle_intro' | 'jingle_outro'): string {
  return `/animation-audio/${BUNDLED_JINGLES[kind].file}`;
}

export function animationAudioUrl(path: string): string {
  return db.storage.from(ANIMATION_AUDIO_BUCKET).getPublicUrl(path).data.publicUrl;
}

// ------------------------------------------------------------ lecture

const soundSchema = z.object({
  path: z.string().min(1).max(260),
  gain: z.number().min(0).max(1),
  duration_ms: z.number().int().nonnegative().nullable().optional(),
  label: z.string().nullable().optional(),
  artist: z.string().nullable().optional(),
  license: z.string().nullable().optional(),
  source_url: z.string().nullable().optional(),
});

/** Volumes communs aux animations et aux vidéos (migration 0677). */
export const animationMixSchema = z.object({
  voice_gain: z.number().min(0).max(3),
  music_gain: z.number().min(0).max(1).nullable(),
  jingle_intro_gain: z.number().min(0).max(1).nullable(),
  jingle_outro_gain: z.number().min(0).max(1).nullable(),
});
export type AnimationMix = z.infer<typeof animationMixSchema>;

export const soundtrackRowSchema = z
  .object({
    music: soundSchema.optional(),
    jingle_intro: soundSchema.optional(),
    jingle_outro: soundSchema.optional(),
    mix: animationMixSchema.optional().catch(undefined),
  })
  .passthrough();

export type SoundtrackRow = z.infer<typeof soundtrackRowSchema>;

/**
 * Volume joué dans l'app pour un son : celui réglé (`mix`), sinon celui du son,
 * rapporté à la voix. La voix ne peut pas jouer plus fort que 100 % dans le
 * navigateur : « voix × 1,5 » = musique et jingles × 1/1,5. PUR.
 */
export function mixedGain(gain: number, voiceGain: number | undefined): number {
  const v = voiceGain && voiceGain > 0 ? voiceGain : 1;
  return Math.round(Math.min(1, Math.max(0, gain / v)) * 1000) / 1000;
}

/** Sons par défaut (base) → bande-son du lecteur ; jingles livrés en repli. */
export function soundtrackFrom(
  row: SoundtrackRow | null | undefined,
  urlOf: (path: string) => string = animationAudioUrl,
): AnimationSoundtrack {
  const mix = row?.mix;
  const voice = mix?.voice_gain;
  const jingle = (kind: 'jingle_intro' | 'jingle_outro') => {
    const s = row?.[kind];
    const own = mix?.[`${kind}_gain`] ?? null;
    return s
      ? { url: urlOf(s.path), gain: mixedGain(own ?? s.gain, voice) }
      : { url: bundledJingleUrl(kind), gain: mixedGain(own ?? DEFAULT_GAIN[kind], voice) };
  };
  return {
    music: row?.music
      ? { url: urlOf(row.music.path), gain: mixedGain(mix?.music_gain ?? row.music.gain, voice) }
      : null,
    jingleIntro: jingle('jingle_intro'),
    jingleOutro: jingle('jingle_outro'),
  };
}

export const soundtrackKeys = { current: ['animation-soundtrack'] as const };

async function fetchSoundtrackRow(): Promise<SoundtrackRow | null> {
  const { data, error } = await db.rpc('get_animation_soundtrack');
  if (error) throw error;
  const parsed = soundtrackRowSchema.safeParse(data ?? {});
  return parsed.success ? parsed.data : null;
}

/** Bande-son des animations (sons par défaut ; relue toutes les 10 min au plus). */
export function useAnimationSoundtrack(): AnimationSoundtrack {
  const q = useQuery({
    queryKey: soundtrackKeys.current,
    queryFn: fetchSoundtrackRow,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  return soundtrackFrom(q.data);
}

/** Volumes réglés (export vidéo) ; `undefined` pendant le chargement, `null` sans réglage. */
export function useAnimationMix(): AnimationMix | null | undefined {
  const q = useQuery({
    queryKey: soundtrackKeys.current,
    queryFn: fetchSoundtrackRow,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  if (q.isPending) return undefined;
  return q.data?.mix ?? null;
}

/** Enregistre les volumes pour l'app et les prochaines vidéos (admin). */
export function useSaveAnimationMix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mix: AnimationMix) => {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await db
        .from('animation_mix_settings')
        .upsert(
          {
            id: true,
            ...mix,
            updated_at: new Date().toISOString(),
            updated_by: auth.user?.id ?? null,
          },
          { onConflict: 'id' },
        );
      if (error) throw new Error('Les volumes n’ont pas pu être enregistrés.');
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: soundtrackKeys.current }),
  });
}

// -------------------------------------------------------------- admin

export const animationAudioRowSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['music', 'jingle_intro', 'jingle_outro']),
  label: z.string(),
  bucket_path: z.string(),
  mime: z.string(),
  bytes: z.number().nullable(),
  duration_ms: z.number().nullable(),
  gain: z.number(),
  is_default: z.boolean(),
  artist: z.string().nullable(),
  license: z.string().nullable(),
  source_url: z.string().nullable(),
  created_at: z.string(),
});
export type AnimationAudioRow = z.infer<typeof animationAudioRowSchema>;

export const audioKeys = {
  all: ['animation-audio'] as const,
  youtube: ['animation-audio', 'youtube-music'] as const,
};

export function useAnimationAudioList() {
  return useQuery({
    queryKey: audioKeys.all,
    queryFn: async (): Promise<AnimationAudioRow[]> => {
      const { data, error } = await db
        .from('animation_audio')
        .select(
          'id, kind, label, bucket_path, mime, bytes, duration_ms, gain, is_default, artist, license, source_url, created_at',
        )
        .order('kind')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return z.array(animationAudioRowSchema).parse(data ?? []);
    },
  });
}

/** Chemin d'un fichier téléversé : `<admin>/<date>-<nom>.<ext>` (minuscules). */
export function audioUploadPath(userId: string, fileName: string, now = Date.now()): string {
  const dot = fileName.lastIndexOf('.');
  const ext = (dot > 0 ? fileName.slice(dot + 1) : 'mp3').toLowerCase();
  const safeExt = ['mp3', 'm4a', 'aac', 'ogg', 'webm'].includes(ext) ? ext : 'mp3';
  const base = (dot > 0 ? fileName.slice(0, dot) : fileName)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${userId.toLowerCase()}/${now}-${base || 'son'}.${safeExt}`;
}

const MIME_OF: Record<string, string> = {
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
};

/** Durée d'un son (ms), lue par le navigateur ; null s'il ne sait pas. */
export function measureAudioMs(blob: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof Audio !== 'function' || typeof URL.createObjectURL !== 'function') {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    const el = new Audio();
    const done = (v: number | null) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    const timer = setTimeout(() => done(null), 5000);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      clearTimeout(timer);
      done(Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : null);
    };
    el.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    el.src = url;
  });
}

export interface NewAudio {
  kind: AnimationAudioKind;
  blob: Blob;
  fileName: string;
  label: string;
  artist?: string | null;
  license?: string | null;
  sourceUrl?: string | null;
  /** chemin imposé (jingles livrés : `defaults/…`) */
  path?: string;
  /** devient le son par défaut de son type */
  makeDefault?: boolean;
}

async function currentUserId(): Promise<string> {
  const { data } = await supabase.auth.getUser();
  if (!data.user) throw new Error('Session expirée : reconnecte-toi.');
  return data.user.id;
}

/** Téléverse un son dans le bucket et l'ajoute à la bibliothèque. */
export async function addAnimationAudio(a: NewAudio): Promise<AnimationAudioRow> {
  const userId = await currentUserId();
  const path = a.path ?? audioUploadPath(userId, a.fileName);
  const ext = path.slice(path.lastIndexOf('.') + 1);
  const mime = MIME_OF[ext] ?? 'audio/mpeg';
  const { error: upErr } = await db.storage
    .from(ANIMATION_AUDIO_BUCKET)
    .upload(path, a.blob, { contentType: mime, upsert: Boolean(a.path) });
  if (upErr) throw new Error(`Téléversement refusé : ${upErr.message}`);
  const durationMs = await measureAudioMs(a.blob);
  const { data, error } = await db
    .from('animation_audio')
    .insert({
      kind: a.kind,
      label: a.label.slice(0, 200),
      bucket_path: path,
      mime,
      bytes: a.blob.size,
      duration_ms: durationMs,
      gain: DEFAULT_GAIN[a.kind],
      artist: a.artist ?? null,
      license: a.license ?? null,
      source_url: a.sourceUrl ?? null,
      created_by: userId,
    })
    .select(
      'id, kind, label, bucket_path, mime, bytes, duration_ms, gain, is_default, artist, license, source_url, created_at',
    )
    .single();
  if (error) throw new Error(error.message);
  const row = animationAudioRowSchema.parse(data);
  if (a.makeDefault) await setAnimationAudioDefault(row.id);
  return { ...row, is_default: Boolean(a.makeDefault) || row.is_default };
}

export async function setAnimationAudioDefault(id: string): Promise<void> {
  const { error } = await db.rpc('set_animation_audio_default', { p_id: id });
  if (error) throw new Error(error.message);
}

function useAudioMutation<T, R>(fn: (arg: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: audioKeys.all });
      void qc.invalidateQueries({ queryKey: soundtrackKeys.current });
    },
  });
}

export function useAddAnimationAudio() {
  return useAudioMutation(addAnimationAudio);
}

export function useSetAnimationAudioDefault() {
  return useAudioMutation(setAnimationAudioDefault);
}

export function useUpdateAnimationAudio() {
  return useAudioMutation(async (p: { id: string; gain?: number; label?: string }) => {
    const patch: Record<string, unknown> = {};
    if (p.gain !== undefined) patch.gain = Math.max(0, Math.min(1, p.gain));
    if (p.label !== undefined) patch.label = p.label.slice(0, 200);
    const { error } = await db.from('animation_audio').update(patch).eq('id', p.id);
    if (error) throw new Error(error.message);
  });
}

export function useDeleteAnimationAudio() {
  return useAudioMutation(async (row: Pick<AnimationAudioRow, 'id' | 'bucket_path'>) => {
    const { error } = await db.from('animation_audio').delete().eq('id', row.id);
    if (error) throw new Error(error.message);
    // le fichier aussi (un jingle livré, `defaults/…`, se réinstalle)
    await db.storage.from(ANIMATION_AUDIO_BUCKET).remove([row.bucket_path]);
  });
}

// ------------------------------------------- médiathèque YouTube, jingles livrés

export const youtubeMusicSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  bucket_path: z.string(),
  mime: z.string(),
  music_artist: z.string().nullable(),
  music_license: z.string().nullable(),
  music_source_url: z.string().nullable(),
});
export type YoutubeMusic = z.infer<typeof youtubeMusicSchema>;

/** Musiques de la médiathèque YouTube Studio (admins). */
export function useYoutubeMusic(enabled = true) {
  return useQuery({
    queryKey: audioKeys.youtube,
    enabled,
    queryFn: async (): Promise<YoutubeMusic[]> => {
      const { data, error } = await db
        .from('youtube_assets')
        .select('id, label, bucket_path, mime, music_artist, music_license, music_source_url')
        .eq('kind', 'music')
        .order('created_at');
      if (error) throw error;
      return z.array(youtubeMusicSchema).parse(data ?? []);
    },
  });
}

/** Nom lisible d'un fichier de musique (« fassounds-lofi-study-…-112191.mp3 » → « Lofi study calm… »). */
export function musicLabel(fileLabel: string): string {
  const base = fileLabel.replace(/\.[a-z0-9]+$/i, '').replace(/-\d{5,}$/, '');
  const words = base.split(/[-_]+/).filter(Boolean);
  const text = words.join(' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : fileLabel;
}

/** Copie une musique de la médiathèque YouTube dans la bibliothèque des animations. */
export function useImportYoutubeMusic() {
  return useAudioMutation(async (p: { music: YoutubeMusic; makeDefault: boolean }) => {
    const { data: blob, error } = await db.storage
      .from('youtube-assets')
      .download(p.music.bucket_path);
    if (error || !blob) throw new Error(`Lecture impossible : ${error?.message ?? 'fichier vide'}`);
    return addAnimationAudio({
      kind: 'music',
      blob,
      fileName: p.music.label,
      label: musicLabel(p.music.label),
      artist: p.music.music_artist,
      license:
        p.music.music_license ??
        (p.music.music_source_url?.includes('pixabay.com') ? 'Pixabay Content License' : null),
      sourceUrl: p.music.music_source_url,
      makeDefault: p.makeDefault,
    });
  });
}

/** Installe dans la bibliothèque un jingle livré avec l'app (`defaults/…`). */
export function useInstallBundledJingle() {
  return useAudioMutation(
    async (p: { kind: 'jingle_intro' | 'jingle_outro'; makeDefault: boolean }) => {
      const res = await fetch(bundledJingleUrl(p.kind));
      if (!res.ok) throw new Error('Jingle introuvable dans l’app.');
      const blob = await res.blob();
      const { file, label } = BUNDLED_JINGLES[p.kind];
      return addAnimationAudio({
        kind: p.kind,
        blob,
        fileName: file,
        label,
        license: 'Création JeRaisonne',
        path: `defaults/${file}`,
        makeDefault: p.makeDefault,
      });
    },
  );
}
